import {Component, inject, OnInit, signal, TemplateRef} from '@angular/core';
import {DataService} from "../services/data.service";
import {ToastrService} from "ngx-toastr";
import {FormsModule} from "@angular/forms";
import moment from "moment";
import {DatePipe, DecimalPipe} from "@angular/common";
import {TooltipDirective} from "ngx-bootstrap/tooltip";
import Swal from "sweetalert2";
import {InvoiceTypePipe} from "../pipes/invoice-type.pipe";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {AccessService} from "../services/access.service";

@Component({
    selector: 'app-invoice-entry',
    imports: [FormsModule, DecimalPipe, TooltipDirective, InvoiceTypePipe, DatePipe],
    template: `
        <h5 class="mb-3">{{ 'entry' | invoiceType }}</h5>

        <div class="d-flex flex-wrap align-items-center gap-3">
            <select class="form-control" style="width: 200px" [(ngModel)]="id_store">
                <option value="" disabled selected style="display: none;">Bitte auswählen</option>
                @for (store of availableStores; track store.id) {
                    <option [value]="store.id">{{ store.name }}</option>
                }
            </select>

            <input type="date" class="form-control" style="width: 140px" [(ngModel)]="date"/>

            <button type="button" class="btn btn-primary"
                    [disabled]="!id_store || loading()"
                    (click)="createInvoice(id_store, pdfModal)">
                <i class="fa-solid fa-file-invoice me-2"></i>Rechnung erstellen
            </button>
        </div>

        @if (invoices.length > 0) {
            <div class="table-responsive">
                <table class="table table-striped table-hover mt-3 mb-0 pointer">
                    <thead>
                    <tr>
                        <th style="width: 190px">Aktionen</th>
                        <th>
                            Store
                        </th>
                        <th class="text-end">
                            Einstiegsgebühr (€)
                        </th>
                        <th>
                            Rechnungsnummer
                        </th>
                        <th>
                            Versendet am
                        </th>
                        <th>
                            Bezahlt am
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                        @for (invoice of invoices; track invoice) {
                            <tr>
                                <td>
                                    @if (invoice.sent_at == null) {
                                        <button type="button" class="btn btn-sm btn-outline-danger me-2" tooltip="Rechnung löschen" (click)="deleteInvoice(invoice)" [disabled]="loading()">
                                            <i class="fa-solid fa-trash"></i>
                                        </button>
                                    }
                                    <button type="button" class="btn btn-sm btn-outline-primary me-2" tooltip="Rechnung senden" (click)="sendInvoice(invoice)" [disabled]="loading()">
                                        <i class="fa-solid fa-envelope"></i>
                                    </button>
                                    <button type="button" class="btn btn-sm btn-outline-primary me-2" tooltip="Rechnung anzeigen" (click)="createInvoice(invoice.id_store, pdfModal)" [disabled]="loading()">
                                        <i class="fa-solid fa-file-invoice"></i>
                                    </button>
                                    <button type="button" class="btn btn-sm btn-outline-primary" tooltip="Begrüßungsschreiben anzeigen" (click)="createWelcomeLetter(invoice.id_store, pdfModal)" [disabled]="loading()">
                                        <i class="fa-solid fa-file-lines"></i>
                                    </button>
                                </td>
                                <td>{{ invoice.store_name }}</td>
                                <td class="text-end">
                                    {{ invoice.entry_charge | number: '1.0' }}
                                </td>
                                <td>
                                    {{ invoice.invoice_number }}
                                </td>
                                <td>
                                    {{ invoice.sent_at | date: 'dd.MM.yyyy' }}
                                    @if (invoice.sent_at == null) {
                                        <span class="text-muted">Noch nicht versendet</span>
                                    }
                                </td>
                                <td>
                                    {{ invoice.paid_at | date: 'dd.MM.yyyy' }}
                                    @if (invoice.paid_at == null) {
                                        <span class="text-muted me-2">Noch nicht bezahlt</span>
                                        <button type="button" class="btn btn-sm btn-outline-primary" tooltip="Datum erfassen" [disabled]="loading()" (click)="setPaidDate(invoice)">
                                            <i class="fa-solid fa-money-bill"></i>
                                        </button>
                                    }
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        } @else {
            <div class="mt-3">Noch keine Rechnungen erstellt</div>
        }

        <ng-template #pdfModal>
            <div class="modal-header">
                <h4 class="modal-title pull-left">PDF Vorschau</h4>
                <button type="button" class="btn-close close pull-right" aria-label="Close" (click)="modalRef?.hide()">
                    <span aria-hidden="true" class="visually-hidden">&times;</span>
                </button>
            </div>
            <div class="modal-body" style="height: 80vh;">
                @if (pdfUrl) {
                    <iframe [src]="pdfUrl" width="100%" height="100%" frameborder="0"></iframe>
                }
            </div>
        </ng-template>
    `
})
export class InvoiceEntryComponent implements OnInit {

    private dataService = inject(DataService);
    private toastrService = inject(ToastrService);
    private modalService = inject(BsModalService);
    private sanitizer = inject(DomSanitizer);
    private accessService = inject(AccessService);

    modalRef?: BsModalRef;
    pdfUrl: SafeResourceUrl | null = null;
    loading = signal(false);

    stores!: any[];
    availableStores: any[] = [];
    id_store: string = '';
    date = moment().format('YYYY-MM-DD');
    invoices: any[] = [];

    ngOnInit(): void {
        this.listInvoices();
    }

    listInvoices() {
        this.dataService.request("Invoice/getForEntryType").subscribe(response => {
            this.invoices = response.invoices;

            this.dataService.request("Store/listForSelection").subscribe(response => {
                this.stores = response.stores;

                // only list stores without an invoice yet
                this.availableStores = this.stores.filter(store => {
                    return this.invoices.find(invoice => invoice.id_store == store.id) == undefined;
                });
            })
        })
    }

    createInvoice(id_store: any, template: TemplateRef<any>): void {
        this.loading.set(true);
        this.dataService.request("Invoice/createInvoiceEntryType", {
            id_store: id_store,
            year: moment(this.date).year(),
            month: moment(this.date).month(),
        }).subscribe(response => {
            this.loading.set(false);
            if (response?.pdf) {
                const byteCharacters = atob(response.pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {type: 'application/pdf'});
                const url = URL.createObjectURL(blob);

                this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                const currentModalRef = this.modalService.show(template, {class: 'modal-xl'});
                this.modalRef = currentModalRef;

                const subscription = this.modalService.onHidden.subscribe((modalRef: any) => {
                    if (modalRef === currentModalRef) {
                        this.pdfUrl = null;
                        URL.revokeObjectURL(url);
                        subscription.unsubscribe();
                    }
                });
            }

            this.listInvoices();
            this.id_store = '';
        });
    }

    createWelcomeLetter(id_store: any, template: TemplateRef<any>): void {
        this.loading.set(true);
        this.dataService.request("Invoice/createWelcomeLetter", {
            id_store: id_store,
            year: moment(this.date).year(),
            month: moment(this.date).month(),
        }).subscribe(response => {
            this.loading.set(false);
            if (response?.pdf) {
                const byteCharacters = atob(response.pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {type: 'application/pdf'});
                const url = URL.createObjectURL(blob);

                this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                const currentModalRef = this.modalService.show(template, {class: 'modal-xl'});
                this.modalRef = currentModalRef;

                const subscription = this.modalService.onHidden.subscribe((modalRef: any) => {
                    if (modalRef === currentModalRef) {
                        this.pdfUrl = null;
                        URL.revokeObjectURL(url);
                        subscription.unsubscribe();
                    }
                });
            }

            this.listInvoices();
        });
    }

    protected deleteInvoice(invoice: any) {
        Swal.fire({
            title: 'Sicher?',
            text: 'Die Rechnung wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request("Invoice/delete", {
                    invoice: invoice,
                }).subscribe(response => {
                    this.listInvoices();
                });
            }
        });
    }

    protected sendInvoice(invoice: any) {
        Swal.fire({
            title: 'Rechnung versenden?',
            text: `Soll die Rechnung versandt werden?`,
            icon: 'question',
            input: 'checkbox',
            inputValue: 1,
            inputPlaceholder: 'Begrüßungsschreiben anhängen',
            showCancelButton: true,
            confirmButtonText: 'Versenden',
            cancelButtonText: 'Abbrechen',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed) {
                this.dataService.request("Invoice/sendInvoiceEntryType", {
                    invoice: invoice,
                    appendWelcomeLetter: !!result.value
                }).subscribe(response => {
                    if (response?.error) {
                        alert('Error: ' + response.error);
                        return;
                    }

                    this.listInvoices();

                    this.toastrService.success("Die Rechnung wurde versandt!");
                });
            }
        });
    }

    protected setPaidDate(invoice: any) {
        Swal.fire({
            title: 'Bezahlt am',
            input: 'date',
            inputValue: moment().format('YYYY-MM-DD'),
            showCancelButton: true,
            confirmButtonText: 'Speichern',
            cancelButtonText: 'Abbrechen',
            confirmButtonColor: this.accessService.client.primaryColor,
            cancelButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                this.loading.set(true);
                this.dataService.request("Invoice/setPaidDateEntry", {
                    id_store: invoice.id_store,
                    id_invoice_type: 3, // Entry
                    paid_at: result.value
                }).subscribe(response => {
                    this.loading.set(false);
                    this.toastrService.success("Datum wurde gespeichert!");
                    this.listInvoices();
                });
            }
        });
    }
}
